import { supabase, Movimiento } from '../lib/supabase';
import { CategoriaEgreso } from '../config/gastos';
import { OwnerContext } from '../types/actividad';
import type { RespaldoMovimiento } from '../types/comprobante';
import { fechaInicioMes, fechaFinMes } from '../utils/dateHelpers';
import { parseDateOnly } from '../utils/formatters';
import { calcularTotales, esIngresoPropio, filtrarQueAfectanBalance } from '../utils/balanceCalculations';

/** Totales de un período. El financiamiento de deudas va aparte de los ingresos y suma al balance. */
export interface EstadisticasBalance {
  ingresos: number;
  egresos: number;
  financiamiento: number;
  balance: number;
  cuotaIglesiaRecaudada: number;
}

// La constante es una concatenación (tipo `string`, no literal): supabase-js no la parsea y tipa el
// resultado como GenericStringError, así que los lectores castean a Movimiento[] (servicios.md).
/** Movimiento con los resúmenes de sus comprobantes, para mostrar páginas y abrirlos al primer toque. */
export const SELECT_MOVIMIENTO_CON_COMPROBANTES =
  '*, comprobante:comprobantes!movimientos_comprobante_id_fkey(id, tipo_archivo, rutas, nombre_original), '
  + 'comprobante_reembolso:comprobantes!movimientos_reembolso_comprobante_id_fkey(id, tipo_archivo, rutas, nombre_original)';

export interface MovimientoFilters {
  tipo?: 'ingreso' | 'egreso';
  origenTipo?: 'miembro' | 'externo' | 'actividad' | 'campaña' | 'financiamiento';
  fechaInicio?: string;
  fechaFin?: string;
  montoMinimo?: number;
  montoMaximo?: number;
  busqueda?: string;
  incluirAnulados?: boolean;
}

export interface PaginatedResponse {
  data: Movimiento[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

interface AporteDobleInput {
  readonly lumisialId: string;
  readonly miembroId: string;
  readonly fecha: string;
  readonly montoAporte: number;
  readonly montoCuota: number;
  readonly descripcion: string;
  readonly respaldo: RespaldoMovimiento;
}

export const movimientoService = {
  async getByLumisial(lumisialId: string, limit?: number): Promise<Movimiento[]> {
    let query = supabase
      .from('movimientos')
      .select('*')
      .eq('lumisial_id', lumisialId)
      .eq('anulado', false)
      .order('fecha', { ascending: false })
      .order('created_at', { ascending: false });

    if (limit) {
      query = query.limit(limit);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data || [];
  },

  async getPaginated(
    lumisialId: string,
    page: number = 1,
    pageSize: number = 20,
    filters?: MovimientoFilters
  ): Promise<PaginatedResponse> {
    let query = supabase
      .from('movimientos')
      .select(SELECT_MOVIMIENTO_CON_COMPROBANTES, { count: 'exact' })
      .eq('lumisial_id', lumisialId);

    // Por defecto excluir anulados, a menos que se solicite incluirlos
    if (!filters?.incluirAnulados) {
      query = query.eq('anulado', false);
    }

    if (filters?.tipo) {
      query = query.eq('tipo', filters.tipo);
    }

    if (filters?.origenTipo) {
      query = query.eq('origen_tipo', filters.origenTipo);
    }

    if (filters?.fechaInicio) {
      query = query.gte('fecha', filters.fechaInicio);
    }

    if (filters?.fechaFin) {
      query = query.lte('fecha', filters.fechaFin);
    }

    if (filters?.montoMinimo) {
      query = query.gte('monto', filters.montoMinimo);
    }

    if (filters?.montoMaximo) {
      query = query.lte('monto', filters.montoMaximo);
    }

    if (filters?.busqueda) {
      const searchTerm = filters.busqueda.toLowerCase();
      query = query.or(`descripcion.ilike.%${searchTerm}%,nombre_externo.ilike.%${searchTerm}%`);
    }

    const { data, error, count } = await query
      .order('fecha', { ascending: false })
      .order('created_at', { ascending: false })
      .range((page - 1) * pageSize, page * pageSize - 1);

    if (error) throw error;

    const total = count || 0;
    const totalPages = Math.ceil(total / pageSize);

    return {
      data: (data ?? []) as unknown as Movimiento[],
      total,
      page,
      pageSize,
      totalPages
    };
  },

  /** Un movimiento con los resúmenes de sus comprobantes, para pantallas que lo reciben sin ellos. */
  async getConComprobantes(id: string): Promise<Movimiento | null> {
    const { data, error } = await supabase
      .from('movimientos')
      .select(SELECT_MOVIMIENTO_CON_COMPROBANTES)
      .eq('id', id)
      .maybeSingle();
    if (error) throw error;
    return data as unknown as Movimiento | null;
  },

  async create(movimiento: Omit<Movimiento, 'id' | 'created_at'>): Promise<Movimiento> {
    const { data, error } = await supabase
      .from('movimientos')
      .insert(movimiento)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  /** Un solo INSERT/HTTP: ambos registros se confirman o se revierten juntos. */
  async crearAporteDoble(input: AporteDobleInput): Promise<void> {
    const comunes = {
      fecha: input.fecha, tipo: 'ingreso' as const,
      lumisial_id: input.lumisialId, origen_tipo: 'miembro' as const, origen_id: input.miembroId,
      requiere_reembolso: false, reembolsado: false, anulado: false,
      ...input.respaldo,
    };
    const movimientos: Omit<Movimiento, 'id' | 'created_at'>[] = [
      { ...comunes, monto: input.montoAporte, categoria_aporte: 'aporte_mensual',
        descripcion: input.descripcion.trim() || 'Aporte Lumisial', afecta_balance: true },
      { ...comunes, monto: input.montoCuota, categoria_aporte: 'cuota_iglesia',
        descripcion: input.descripcion.trim() || 'Cuota Iglesia', afecta_balance: false },
    ];
    const { error } = await supabase.from('movimientos').insert(movimientos);
    if (error) throw error;
  },

  async update(id: string, movimiento: Partial<Omit<Movimiento, 'id' | 'created_at'>>): Promise<Movimiento> {
    const { data, error } = await supabase
      .from('movimientos')
      .update(movimiento)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async delete(id: string): Promise<void> {
    const { data: movimientoActual } = await supabase
      .from('movimientos')
      .select('origen_tipo')
      .eq('id', id)
      .maybeSingle();

    if (movimientoActual?.origen_tipo === 'actividad') {
      throw new Error('No se pueden eliminar manualmente los movimientos de actividades economicas. Elimina el gasto desde la gestion de la actividad.');
    }

    const { error } = await supabase
      .from('movimientos')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  /**
   * Helper function to calculate balance statistics from movimientos data
   */
  _calcularEstadisticasBalance(data: Array<{ tipo: string; monto: number; origen_tipo?: string; categoria_aporte?: string; afecta_balance?: boolean }>): EstadisticasBalance {
    const cuotaIglesiaRecaudada = data
      .filter(m => m.tipo === 'ingreso' && m.origen_tipo === 'miembro' && m.categoria_aporte === 'cuota_iglesia')
      .reduce((sum, m) => sum + Number(m.monto), 0);

    // El financiamiento de una deuda no es ingreso propio, pero sí suma al balance.
    const { ingresos, egresos, financiamiento, balance } = calcularTotales(filtrarQueAfectanBalance(data));

    return {
      ingresos,
      egresos,
      financiamiento,
      balance,
      cuotaIglesiaRecaudada
    };
  },

  async getBalanceByLumisial(lumisialId: string): Promise<EstadisticasBalance> {
    const { data, error } = await supabase
      .from('movimientos')
      .select('tipo, monto, origen_tipo, categoria_aporte, afecta_balance')
      .eq('lumisial_id', lumisialId)
      .eq('anulado', false);

    if (error) throw error;

    return this._calcularEstadisticasBalance(data || []);
  },

  async getStatsAndRecent(lumisialId: string, recentLimit: number = 5): Promise<{
    balance: EstadisticasBalance & { cuotaIglesiaJal: number; cuotaIglesiaPA: number };
    recentMovimientos: Movimiento[];
    cantidadIngresos: number;
    cantidadEgresos: number;
    cantidadCuotaIglesia: number;
  }> {
    const [allMovimientos, recentMovimientos] = await Promise.all([
      supabase
        .from('movimientos')
        .select('tipo, monto, origen_tipo, categoria_aporte, afecta_balance, modo_pago_cuota_iglesia')
        .eq('lumisial_id', lumisialId)
        .eq('anulado', false),
      supabase
        .from('movimientos')
        .select('*')
        .eq('lumisial_id', lumisialId)
        .eq('anulado', false)
        .order('fecha', { ascending: false })
        .order('created_at', { ascending: false })
        .limit(recentLimit)
    ]);

    if (allMovimientos.error) throw allMovimientos.error;
    if (recentMovimientos.error) throw recentMovimientos.error;

    const data = allMovimientos.data || [];

    const movimientosCuota = data.filter(m => m.tipo === 'ingreso' && m.origen_tipo === 'miembro' && m.categoria_aporte === 'cuota_iglesia');
    const cuotaIglesiaRecaudada = movimientosCuota.reduce((sum, m) => sum + Number(m.monto), 0);
    const cuotaIglesiaJal = movimientosCuota.filter(m => m.modo_pago_cuota_iglesia == null).reduce((sum, m) => sum + Number(m.monto), 0);
    const cuotaIglesiaPA = movimientosCuota.filter(m => m.modo_pago_cuota_iglesia != null).reduce((sum, m) => sum + Number(m.monto), 0);

    const afectanBalance = filtrarQueAfectanBalance(data);
    const movimientosIngresos = afectanBalance.filter(esIngresoPropio);
    const movimientosEgresos = afectanBalance.filter(m => m.tipo === 'egreso');

    const movimientosCuotaIglesia = data.filter(m =>
      m.tipo === 'ingreso' && m.origen_tipo === 'miembro' && m.categoria_aporte === 'cuota_iglesia'
    );

    // El financiamiento de una deuda no es ingreso propio, pero sí suma al balance.
    const { ingresos, egresos, financiamiento, balance } = calcularTotales(afectanBalance);

    return {
      balance: {
        ingresos,
        egresos,
        financiamiento,
        balance,
        cuotaIglesiaRecaudada,
        cuotaIglesiaJal,
        cuotaIglesiaPA
      },
      recentMovimientos: recentMovimientos.data || [],
      cantidadIngresos: movimientosIngresos.length,
      cantidadEgresos: movimientosEgresos.length,
      cantidadCuotaIglesia: movimientosCuotaIglesia.length
    };
  },

  async getBalanceByPeriod(lumisialId: string, fechaInicio: string, fechaFin: string): Promise<EstadisticasBalance> {
    const { data, error } = await supabase
      .from('movimientos')
      .select('tipo, monto, origen_tipo, categoria_aporte, afecta_balance')
      .eq('lumisial_id', lumisialId)
      .eq('anulado', false)
      .gte('fecha', fechaInicio)
      .lte('fecha', fechaFin);

    if (error) throw error;

    return this._calcularEstadisticasBalance(data || []);
  },

  async getGastosPorCategoria(lumisialId: string, categoriaEgreso: CategoriaEgreso): Promise<Movimiento[]> {
    const { data, error } = await supabase
      .from('movimientos')
      .select('*')
      .eq('lumisial_id', lumisialId)
      .eq('tipo', 'egreso')
      .eq('categoria_egreso', categoriaEgreso)
      .eq('anulado', false)
      .order('fecha', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async getGastosPendientesReembolso(lumisialId: string): Promise<Movimiento[]> {
    const { data, error } = await supabase
      .from('movimientos')
      .select('*')
      .eq('lumisial_id', lumisialId)
      .eq('tipo', 'egreso')
      .eq('requiere_reembolso', true)
      .eq('reembolsado', false)
      .eq('anulado', false)
      .order('fecha', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async getByMiembro(lumisialId: string, miembroId: string): Promise<Movimiento[]> {
    const { data, error } = await supabase
      .from('movimientos')
      .select('*')
      .eq('lumisial_id', lumisialId)
      .eq('origen_tipo', 'miembro')
      .eq('origen_id', miembroId)
      .eq('anulado', false)
      .order('fecha', { ascending: false })
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  /**
   * Anula un movimiento (soft delete)
   * @param id - ID del movimiento a anular
   * @param motivo - Razón de la anulación
   * @returns El movimiento anulado
   */
  async anular(id: string, motivo?: string): Promise<Movimiento> {
    const ahora = new Date().toISOString();

    const { data, error } = await supabase
      .from('movimientos')
      .update({
        anulado: true,
        motivo_anulacion: motivo || 'Movimiento anulado',
        fecha_anulacion: ahora
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async getPaginatedMovimientosJA(
    ownerContext: OwnerContext,
    filters: MovimientoFilters,
    page: number = 1,
    pageSize: number = 20
  ): Promise<PaginatedResponse> {
    let query = supabase
      .from('movimientos')
      .select(SELECT_MOVIMIENTO_CON_COMPROBANTES, { count: 'exact' })
      .is('lumisial_id', null);

    if (ownerContext.tipo === 'diocesis') {
      query = query.eq('diocesis_id', ownerContext.id);
    } else if (ownerContext.tipo === 'provincia') {
      query = query.eq('provincia_eclesiastica_id', ownerContext.id);
    }

    if (!filters.incluirAnulados) {
      query = query.eq('anulado', false);
    }

    if (filters.tipo) {
      query = query.eq('tipo', filters.tipo);
    }

    if (filters.origenTipo) {
      query = query.eq('origen_tipo', filters.origenTipo);
    }

    if (filters.fechaInicio) {
      query = query.gte('fecha', filters.fechaInicio);
    }

    if (filters.fechaFin) {
      query = query.lte('fecha', filters.fechaFin);
    }

    if (filters.montoMinimo) {
      query = query.gte('monto', filters.montoMinimo);
    }

    if (filters.montoMaximo) {
      query = query.lte('monto', filters.montoMaximo);
    }

    if (filters.busqueda) {
      const searchTerm = filters.busqueda.toLowerCase();
      query = query.or(`descripcion.ilike.%${searchTerm}%,nombre_externo.ilike.%${searchTerm}%`);
    }

    const { data, error, count } = await query
      .order('fecha', { ascending: false })
      .order('created_at', { ascending: false })
      .range((page - 1) * pageSize, page * pageSize - 1);

    if (error) throw error;

    const total = count || 0;
    const totalPages = Math.ceil(total / pageSize);

    return { data: (data ?? []) as unknown as Movimiento[], total, page, pageSize, totalPages };
  },

  async confirmarPagoAutomaticoPA(params: {
    miembroId: string;
    lumisialId: string;
    monto: number;
    fecha: string;
    modoPago: string;
    miembroNombre: string;
    usuarioId: string;
    esRegistroExterno?: boolean;
    respaldo: RespaldoMovimiento;
  }): Promise<{ movimientoJAL: Movimiento }> {
    const { miembroId, lumisialId, monto, fecha, modoPago, miembroNombre, usuarioId, esRegistroExterno } = params;

    // Guardia: verificar que no exista PA confirmado para este miembro en el mismo período
    const fechaPago = parseDateOnly(fecha);
    const fechaInicioPeriodo = fechaInicioMes(fechaPago.getMonth() + 1, fechaPago.getFullYear());
    const fechaFinPeriodo = fechaFinMes(fechaPago.getMonth() + 1, fechaPago.getFullYear());

    const { data: paExistente } = await supabase
      .from('movimientos')
      .select('id')
      .eq('lumisial_id', lumisialId)
      .eq('origen_id', miembroId)
      .eq('categoria_aporte', 'cuota_iglesia')
      .not('modo_pago_cuota_iglesia', 'is', null)
      .eq('anulado', false)
      .gte('fecha', fechaInicioPeriodo)
      .lte('fecha', fechaFinPeriodo)
      .maybeSingle();

    if (paExistente) {
      throw new Error('Este miembro ya tiene un pago automático (PA) registrado para este período.');
    }

    // Crear movimiento JAL (afecta_balance: false — tránsito, JAD y JAN se generan al aprobar balance)
    const { data: movJAL, error: errorJAL } = await supabase
      .from('movimientos')
      .insert({
        lumisial_id: lumisialId,
        tipo: 'ingreso' as const,
        monto,
        fecha,
        origen_tipo: 'miembro' as const,
        origen_id: miembroId,
        categoria_aporte: 'cuota_iglesia' as const,
        modo_pago_cuota_iglesia: modoPago,
        afecta_balance: false,
        descripcion: `Pago automático cuota iglesia — ${miembroNombre}`,
        registrado_por_usuario_id: usuarioId,
        es_registro_externo: esRegistroExterno ?? false,
        anulado: false,
        requiere_reembolso: false,
        reembolsado: false,
        ...params.respaldo,
      })
      .select()
      .single();

    if (errorJAL) throw errorJAL;

    return { movimientoJAL: movJAL };
  },
};
